// Area code -> IANA time zone(s) for every NANP (+1) area code in service.
//
// Sources (fetched 2026-09-02):
//   - https://en.wikipedia.org/wiki/List_of_North_American_Numbering_Plan_area_codes  (code -> state/province, in-service status)
//   - https://raw.githubusercontent.com/google/libphonenumber/master/resources/timezones/map_data.txt  (prefix -> zones, unioned per area code)
//   - split-state overrides maintained by hand from the per-state time zone articles (see OVERRIDES in the build script).
//
// Semantics: quiet hours must hold in EVERY listed zone, so an area code that spans two zones lists both.
// Listing an extra zone is safe; missing one is not. Generated by packages/core/scripts/build_areacodes.py, then hand-reviewed.
// Non-geographic codes (toll-free, premium, personal) are deliberately absent and resolve to [].

const NY = 'America/New_York';
const CHI = 'America/Chicago';
const DEN = 'America/Denver';
const PHX = 'America/Phoenix';
const LAX = 'America/Los_Angeles';
const BOISE = 'America/Boise';
const ANC = 'America/Anchorage';
const ADAK = 'America/Adak';
const HNL = 'Pacific/Honolulu';
const TOR = 'America/Toronto';
const WPG = 'America/Winnipeg';
const REG = 'America/Regina';
const EDM = 'America/Edmonton';
const VAN = 'America/Vancouver';
const HAL = 'America/Halifax';
const STJ = 'America/St_Johns';
const GOOSE = 'America/Goose_Bay';
const BLANC = 'America/Blanc-Sablon';
const ATIK = 'America/Atikokan';
const DAWSON = 'America/Dawson_Creek';
const FORTN = 'America/Fort_Nelson';
const CRESTON = 'America/Creston';
const WHITEHORSE = 'America/Whitehorse';
const IQALUIT = 'America/Iqaluit';
const RANKIN = 'America/Rankin_Inlet';
const CAMBRIDGE = 'America/Cambridge_Bay';

const TABLE: Record<string, readonly string[]> = {
  '201': [NY], // NJ
  '202': [NY], // DC
  '203': [NY], // CT
  '204': [WPG], // MB
  '205': [CHI], // AL
  '206': [LAX], // WA
  '207': [NY], // ME
  '208': [BOISE, LAX], // ID
  '209': [LAX], // CA
  '210': [CHI], // TX
  '212': [NY], // NY
  '213': [LAX], // CA
  '214': [CHI], // TX
  '215': [NY], // PA
  '216': [NY], // OH
  '217': [CHI], // IL
  '218': [CHI], // MN
  '219': [NY, CHI], // IN
  '220': [NY], // OH
  '223': [NY], // PA
  '224': [CHI], // IL
  '225': [CHI], // LA
  '226': [TOR], // ON
  '227': [NY], // MD
  '228': [CHI], // MS
  '229': [NY], // GA
  '231': [NY], // MI
  '234': [NY], // OH
  '235': [CHI], // MO
  '236': [VAN, EDM, DAWSON, FORTN, CRESTON], // BC
  '239': [NY], // FL
  '240': [NY], // MD
  '242': ['America/Nassau'], // BS
  '246': ['America/Barbados'], // BB
  '248': [NY], // MI
  '249': [TOR], // ON
  '250': [VAN, EDM, DAWSON, FORTN, CRESTON], // BC
  '251': [CHI], // AL
  '252': [NY], // NC
  '253': [LAX], // WA
  '254': [CHI], // TX
  '256': [CHI], // AL
  '257': [VAN], // BC
  '260': [NY], // IN
  '262': [CHI], // WI
  '263': [TOR], // QC
  '264': ['America/Anguilla'], // AI
  '267': [NY], // PA
  '268': ['America/Antigua'], // AG
  '269': [NY], // MI
  '270': [CHI, NY], // KY
  '272': [NY], // PA
  '273': [TOR, HAL, BLANC], // QC
  '274': [CHI], // WI
  '276': [NY], // VA
  '279': [LAX], // CA
  '281': [CHI], // TX
  '283': [NY], // OH
  '284': ['America/Tortola'], // VG
  '289': [TOR], // ON
  '301': [NY], // MD
  '302': [NY], // DE
  '303': [DEN], // CO
  '304': [NY], // WV
  '305': [NY], // FL
  '306': [REG, EDM, WPG], // SK
  '307': [DEN], // WY
  '308': [CHI, DEN], // NE
  '309': [CHI], // IL
  '310': [LAX], // CA
  '312': [CHI], // IL
  '313': [NY], // MI
  '314': [CHI], // MO
  '315': [NY], // NY
  '316': [CHI], // KS
  '317': [NY], // IN
  '318': [CHI], // LA
  '319': [CHI], // IA
  '320': [CHI], // MN
  '321': [NY], // FL
  '323': [LAX], // CA
  '324': [NY], // FL
  '325': [CHI], // TX
  '326': [NY], // OH
  '327': [CHI], // AR
  '329': [NY], // NY
  '330': [NY], // OH
  '331': [CHI], // IL
  '332': [NY], // NY
  '334': [CHI, NY], // AL
  '336': [NY], // NC
  '337': [CHI], // LA
  '339': [NY], // MA
  '340': ['America/St_Thomas'], // VI
  '341': [LAX], // CA
  '343': [TOR], // ON
  '345': ['America/Cayman'], // KY
  '346': [CHI], // TX
  '347': [NY], // NY
  '350': [LAX], // CA
  '351': [NY], // MA
  '352': [NY], // FL
  '353': [CHI], // WI
  '354': [TOR], // QC
  '357': [LAX], // CA
  '360': [LAX], // WA
  '361': [CHI], // TX
  '363': [NY], // NY
  '364': [CHI, NY], // KY
  '365': [TOR], // ON
  '367': [TOR, HAL, BLANC], // QC
  '368': [EDM], // AB
  '369': [LAX], // CA
  '380': [NY], // OH
  '382': [TOR], // ON
  '385': [DEN], // UT
  '386': [NY], // FL
  '387': [TOR], // ON
  '401': [NY], // RI
  '402': [CHI], // NE
  '403': [EDM], // AB
  '404': [NY], // GA
  '405': [CHI], // OK
  '406': [DEN], // MT
  '407': [NY], // FL
  '408': [LAX], // CA
  '409': [CHI], // TX
  '410': [NY], // MD
  '412': [NY], // PA
  '413': [NY], // MA
  '414': [CHI], // WI
  '415': [LAX], // CA
  '416': [TOR], // ON
  '417': [CHI], // MO
  '418': [TOR, HAL, BLANC], // QC
  '419': [NY], // OH
  '423': [NY, CHI], // TN
  '424': [LAX], // CA
  '425': [LAX], // WA
  '428': [HAL], // NB
  '430': [CHI], // TX
  '431': [WPG], // MB
  '432': [CHI, DEN], // TX
  '434': [NY], // VA
  '435': [DEN], // UT
  '436': [NY], // OH
  '437': [TOR], // ON
  '438': [TOR], // QC
  '440': [NY], // OH
  '441': ['Atlantic/Bermuda'], // BM
  '442': [LAX], // CA
  '443': [NY], // MD
  '445': [NY], // PA
  '447': [CHI], // IL
  '448': [NY, CHI], // FL
  '450': [TOR], // QC
  '457': [CHI], // LA
  '458': [LAX, BOISE], // OR
  '460': [TOR], // ON
  '463': [NY], // IN
  '464': [CHI], // IL
  '465': [NY], // NY
  '468': [TOR], // QC
  '469': [CHI], // TX
  '470': [NY], // GA
  '471': [CHI], // MS
  '472': [NY], // NC
  '473': ['America/Grenada'], // GD
  '474': [REG, EDM, WPG], // SK
  '475': [NY], // CT
  '478': [NY], // GA
  '479': [CHI], // AR
  '480': [PHX], // AZ
  '483': [CHI, NY], // AL
  '484': [NY], // PA
  '487': [TOR], // ON
  '501': [CHI], // AR
  '502': [NY], // KY
  '503': [LAX], // OR
  '504': [CHI], // LA
  '505': [DEN], // NM
  '506': [HAL], // NB
  '507': [CHI], // MN
  '508': [NY], // MA
  '509': [LAX], // WA
  '510': [LAX], // CA
  '512': [CHI], // TX
  '513': [NY], // OH
  '514': [TOR], // QC
  '515': [CHI], // IA
  '516': [NY], // NY
  '517': [NY], // MI
  '518': [NY], // NY
  '519': [TOR], // ON
  '520': [PHX], // AZ
  '530': [LAX], // CA
  '531': [CHI], // NE
  '534': [CHI], // WI
  '537': [TOR], // ON
  '539': [CHI], // OK
  '540': [NY], // VA
  '541': [LAX, BOISE], // OR
  '548': [TOR], // ON
  '551': [NY], // NJ
  '557': [CHI], // MO
  '559': [LAX], // CA
  '561': [NY], // FL
  '562': [LAX], // CA
  '563': [CHI], // IA
  '564': [LAX], // WA
  '565': [NY], // GA
  '567': [NY], // OH
  '568': [EDM], // AB
  '570': [NY], // PA
  '571': [NY], // VA
  '572': [CHI], // OK
  '573': [CHI], // MO
  '574': [NY, CHI], // IN
  '575': [DEN], // NM
  '579': [TOR], // QC
  '580': [CHI], // OK
  '581': [TOR, HAL, BLANC], // QC
  '582': [NY], // PA
  '584': [WPG], // MB
  '585': [NY], // NY
  '586': [NY], // MI
  '587': [EDM], // AB
  '601': [CHI], // MS
  '602': [PHX], // AZ
  '603': [NY], // NH
  '604': [VAN], // BC
  '605': [CHI, DEN], // SD
  '606': [NY, CHI], // KY
  '607': [NY], // NY
  '608': [CHI], // WI
  '609': [NY], // NJ
  '610': [NY], // PA
  '612': [CHI], // MN
  '613': [TOR], // ON
  '614': [NY], // OH
  '615': [CHI], // TN
  '616': [NY], // MI
  '617': [NY], // MA
  '618': [CHI], // IL
  '619': [LAX], // CA
  '620': [CHI, DEN], // KS
  '621': [CHI], // TX
  '623': [PHX], // AZ
  '624': [NY], // NY
  '626': [LAX], // CA
  '628': [LAX], // CA
  '629': [CHI], // TN
  '630': [CHI], // IL
  '631': [NY], // NY
  '636': [CHI], // MO
  '639': [REG, EDM, WPG], // SK
  '640': [NY], // NJ
  '641': [CHI], // IA
  '645': [NY], // FL
  '646': [NY], // NY
  '647': [TOR], // ON
  '649': ['America/Grand_Turk'], // TC
  '650': [LAX], // CA
  '651': [CHI], // MN
  '656': [NY], // FL
  '657': [LAX], // CA
  '658': ['America/Jamaica'], // JM
  '659': [CHI], // AL
  '660': [CHI], // MO
  '661': [LAX], // CA
  '662': [CHI], // MS
  '664': ['America/Montserrat'], // MS
  '667': [NY], // MD
  '669': [LAX], // CA
  '670': ['Pacific/Saipan'], // MP
  '671': ['Pacific/Guam'], // GU
  '672': [VAN, EDM, DAWSON, FORTN, CRESTON], // BC
  '678': [NY], // GA
  '679': [NY], // MI
  '680': [NY], // NY
  '681': [NY], // WV
  '682': [CHI], // TX
  '683': [TOR], // ON
  '684': ['Pacific/Pago_Pago'], // AS
  '686': [NY], // VA
  '689': [NY], // FL
  '701': [CHI, DEN], // ND
  '702': [LAX], // NV
  '703': [NY], // VA
  '704': [NY], // NC
  '705': [TOR], // ON
  '706': [NY], // GA
  '707': [LAX], // CA
  '708': [CHI], // IL
  '709': [STJ, GOOSE], // NL
  '712': [CHI], // IA
  '713': [CHI], // TX
  '714': [LAX], // CA
  '715': [CHI], // WI
  '716': [NY], // NY
  '717': [NY], // PA
  '718': [NY], // NY
  '719': [DEN], // CO
  '720': [DEN], // CO
  '721': ['America/Lower_Princes'], // SX
  '724': [NY], // PA
  '725': [LAX], // NV
  '726': [CHI], // TX
  '727': [NY], // FL
  '728': [NY], // FL
  '729': [NY, CHI], // TN
  '730': [CHI], // IL
  '731': [CHI], // TN
  '732': [NY], // NJ
  '734': [NY], // MI
  '737': [CHI], // TX
  '738': [LAX], // CA
  '740': [NY], // OH
  '742': [TOR], // ON
  '743': [NY], // NC
  '747': [LAX], // CA
  '748': [DEN], // CO
  '753': [TOR], // ON
  '754': [NY], // FL
  '757': [NY], // VA
  '758': ['America/St_Lucia'], // LC
  '760': [LAX], // CA
  '762': [NY], // GA
  '763': [CHI], // MN
  '765': [NY], // IN
  '767': ['America/Dominica'], // DM
  '769': [CHI], // MS
  '770': [NY], // GA
  '771': [NY], // DC
  '772': [NY], // FL
  '773': [CHI], // IL
  '774': [NY], // MA
  '775': [LAX, DEN], // NV
  '778': [VAN, EDM, DAWSON, FORTN, CRESTON], // BC
  '779': [CHI], // IL
  '780': [EDM], // AB
  '781': [NY], // MA
  '782': [HAL], // NS
  '784': ['America/St_Vincent'], // VC
  '785': [CHI, DEN], // KS
  '786': [NY], // FL
  '787': ['America/Puerto_Rico'], // PR
  '801': [DEN], // UT
  '802': [NY], // VT
  '803': [NY], // SC
  '804': [NY], // VA
  '805': [LAX], // CA
  '806': [CHI], // TX
  '807': [TOR, WPG, ATIK], // ON
  '808': [HNL], // HI
  '809': ['America/Santo_Domingo'], // DO
  '810': [NY], // MI
  '812': [NY, CHI], // IN
  '813': [NY], // FL
  '814': [NY], // PA
  '815': [CHI], // IL
  '816': [CHI], // MO
  '817': [CHI], // TX
  '818': [LAX], // CA
  '819': [TOR], // QC
  '820': [LAX], // CA
  '821': [NY], // SC
  '825': [EDM], // AB
  '826': [NY], // VA
  '828': [NY], // NC
  '829': ['America/Santo_Domingo'], // DO
  '830': [CHI], // TX
  '831': [LAX], // CA
  '832': [CHI], // TX
  '835': [NY], // PA
  '837': [LAX], // CA
  '838': [NY], // NY
  '839': [NY], // SC
  '840': [LAX], // CA
  '843': [NY], // SC
  '845': [NY], // NY
  '847': [CHI], // IL
  '848': [NY], // NJ
  '849': ['America/Santo_Domingo'], // DO
  '850': [NY, CHI], // FL
  '851': [HAL], // NS
  '854': [NY], // SC
  '856': [NY], // NJ
  '857': [NY], // MA
  '858': [LAX], // CA
  '859': [NY], // KY
  '860': [NY], // CT
  '861': [CHI], // IL
  '862': [NY], // NJ
  '863': [NY], // FL
  '864': [NY], // SC
  '865': [NY], // TN
  '867': [WHITEHORSE, EDM, IQALUIT, RANKIN, CAMBRIDGE], // YT
  '868': ['America/Port_of_Spain'], // TT
  '869': ['America/St_Kitts'], // KN
  '870': [CHI], // AR
  '871': [TOR], // ON
  '872': [CHI], // IL
  '873': [TOR], // QC
  '876': ['America/Jamaica'], // JM
  '878': [NY], // PA
  '879': [STJ, GOOSE], // NL
  '901': [CHI], // TN
  '902': [HAL], // NS
  '903': [CHI], // TX
  '904': [NY], // FL
  '905': [TOR], // ON
  '906': [NY, CHI], // MI
  '907': [ANC, ADAK], // AK
  '908': [NY], // NJ
  '909': [LAX], // CA
  '910': [NY], // NC
  '912': [NY], // GA
  '913': [CHI], // KS
  '914': [NY], // NY
  '915': [DEN], // TX
  '916': [LAX], // CA
  '917': [NY], // NY
  '918': [CHI], // OK
  '919': [NY], // NC
  '920': [CHI], // WI
  '924': [CHI], // MN
  '925': [LAX], // CA
  '928': [PHX, DEN], // AZ
  '929': [NY], // NY
  '930': [NY, CHI], // IN
  '931': [CHI], // TN
  '934': [NY], // NY
  '936': [CHI], // TX
  '937': [NY], // OH
  '938': [CHI], // AL
  '939': ['America/Puerto_Rico'], // PR
  '940': [CHI], // TX
  '941': [NY], // FL
  '942': [TOR], // ON
  '943': [NY], // GA
  '945': [CHI], // TX
  '947': [NY], // MI
  '948': [NY], // VA
  '949': [LAX], // CA
  '951': [LAX], // CA
  '952': [CHI], // MN
  '954': [NY], // FL
  '956': [CHI], // TX
  '959': [NY], // CT
  '970': [DEN], // CO
  '971': [LAX], // OR
  '972': [CHI], // TX
  '973': [NY], // NJ
  '975': [CHI], // MO
  '978': [NY], // MA
  '979': [CHI], // TX
  '980': [NY], // NC
  '983': [DEN], // CO
  '984': [NY], // NC
  '985': [CHI], // LA
  '986': [BOISE, LAX], // ID
  '989': [NY], // MI
};

/**
 * IANA zone names an area code may sit in; more than one when the code spans a time-zone line.
 * Accepts `239`, `+1239`, `1239`, or a full E.164 number. Unknown / non-geographic -> [].
 */
export function timezonesForAreaCode(code: string): string[] {
  const digits = code.replace(/\D+/g, '');
  const key = digits.length === 3 ? digits : digits.length >= 4 && digits.startsWith('1') ? digits.slice(1, 4) : null;
  if (!key) return [];
  const zones = TABLE[key];
  return zones ? [...zones] : [];
}

/** Every area code in the table, sorted. */
export function knownAreaCodes(): string[] {
  return Object.keys(TABLE).sort();
}
